import { useContext } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { API_HTTP } from "./endpoints";
import ApiInterface from "./api-interface";
import { useSnackbar } from "./useSnackbar";
import { resumeAnalyzerApiEndpoints } from "./endpoints";
import type { User } from "firebase/auth";
import { AuthContext } from "../../context/AuthProvider";
import {
	JobCardData,
	JobDetails,
	APPLIED,
	JobCardApiData,
	AddJobApiResponse,
	JobListApiResponse,
	GenerateResumeApiResponse,
	AddJobApiPayload,
	UpdateJobsApiPayload,
	JobState,
	GenerateCoverLetterApiResponse,
	DONE,
	GetLatestCoverLetterApiResponse,
	ArchiveOutcome
} from "../..//DataModels/DataModels";
import useClaims from "./useClaims";

const ONE_MINUTE_DURATION_IN_MS = 60 * 1000;

const UNLIMITED_COVER_LETTERS_PERMISSION = "features/unlimited-cover-letters";
const UNLIMITED_RESUMES_PERMISSION = "features/unlimited-resumes";

/**
 * Transforms an array of `JobCardApiData` objects into `JobCardData` format by normalizing properties.
 *
 * @param jobs - Array of job objects to transform. Each job object should contain:
 *               - jobId: number - The unique identifier for the job
 *               - ...other properties - All other properties are preserved as-is
 *
 * @returns Array of `JobCardData` objects with normalized structure:
 *          - id: The jobId value mapped to id property
 *          - isError: defaults to false
 *          - isPending: defaults to false
 *          - ...other properties preserved from original job object
 *
 * @example
 * // Basic usage with job array
 * const rawJobs = [
 *   { jobId: 123, title: "Software Engineer" },
 *   { jobId: 456, title: "Data Analyst" }
 * ];
 * const jobCards = fixJobCardDate(rawJobs);
 * // Returns: [
 * //   { id: "123", title: "Software Engineer", isError: false, isPending: false },
 * //   { id: "456", title: "Data Analyst", isError: false, isPending: false }
 * // ]
 *
 * @example
 * // Handling empty array
 * const jobCards = fixJobCardDate([]);
 * // Returns: []
 */
function convertJobCardDataType(jobs: JobCardApiData[]): JobCardData[] {
	return jobs.map(({ jobId, order, ...rest }) => ({
		id: jobId.toString(),
		isError: false,
		isPending: false,
		...rest
	}));
}

const DEFAULT_START_JOB_STATE = APPLIED;

export const useServer = () => {
	const authContext = useContext(AuthContext);
	const { showSnack } = useSnackbar();
	const user = authContext?.user as User | undefined;
	const userId = user?.uid;
	const queryClient = useQueryClient();
	const userClaims = useClaims();

	// State derived from useClaims()
	const userPermissions =
		userClaims && Array.isArray(userClaims?.perms) ? userClaims.perms : [];
	const hasUnlimitedCoverLetters = userPermissions.includes(
		UNLIMITED_COVER_LETTERS_PERMISSION
	);
	const hasUnlimitedResumes = userPermissions.includes(
		UNLIMITED_RESUMES_PERMISSION
	);

	//console.log(`useServer render:[${userId}]`)
	const jobsQuery = useQuery<JobCardData[]>({
		queryKey: ["v2", "user", user?.uid, "jobs"],
		queryFn: async () => {
			if (!user?.uid) throw new Error("User not authenticated");
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			const res: JobListApiResponse = await apiClient.get(
				resumeAnalyzerApiEndpoints.jobs
			);
			const jobs = [...res.doneJobsList, ...res.jobs];
			return convertJobCardDataType(jobs);
		},
		enabled: !!userId,
		staleTime: ONE_MINUTE_DURATION_IN_MS
	});

	const getJobs = () => {
		return {
			jobs: jobsQuery.data ?? [],
			isLoading: jobsQuery.isLoading,
			error: jobsQuery.error
		};
	};

	interface PostJobData {
		company: string;
		title: string;
		jobDescription: string;
	}

	//https://keysandcastles-jason-mcburney-27c433854a5315ae0d36dddb2effab58b.gitlab.io/#operation/add-job
	const createJobMutation = useMutation({
		mutationFn: async (jobData: PostJobData): Promise<AddJobApiResponse> => {
			const apiClient = new ApiInterface(
				API_HTTP.resumeAnalyzer,
				user ?? undefined
			);
			const payload: AddJobApiPayload = {
				...jobData,
				state: DEFAULT_START_JOB_STATE
			};
			return apiClient.post(resumeAnalyzerApiEndpoints.jobs, payload);
		},
		onMutate: (jobData: PostJobData) => {
			const rVal: JobCardData = {
				...jobData,
				id: "",
				placeholderId: crypto.randomUUID(),
				creationTimestamp: new Date().toISOString(),
				state: DEFAULT_START_JOB_STATE,
				isError: false,
				isPending: true
			};

			//Optimistically update
			queryClient.setQueryData(
				["v2", "user", user?.uid, "jobs"],
				(old: JobCardData[] | undefined): typeof old =>
					old ? [rVal, ...old] : old
			);

			//Rollback context
			return {
				theJob: rVal
			};
		},
		onSuccess: (mutationResult, jobdata: PostJobData, context) => {
			//Clear the optimistic element and requery

			const finalJob = {
				...context.theJob,
				id: mutationResult.jobId.toString(),
				placeholderId: undefined,
				isPending: false
			};

			//Update Cache with new Info.
			queryClient.setQueryData(
				["v2", "user", user?.uid, "jobs"],
				(old: JobCardData[] | undefined): typeof old =>
					old
						? [
								finalJob,
								...old.filter(
									(j) => j.placeholderId !== context.theJob.placeholderId
								)
							]
						: old
			);

			showSnack("NotifyJobAdded", "dark");
		},
		onError: (error, inputvars, context) => {
			//RollBack
			queryClient.setQueryData(
				["v2", "user", user?.uid, "jobs"],
				(old: JobCardData[] | undefined) =>
					old?.filter((j) => j.placeholderId !== context?.theJob.placeholderId)
			);
			console.log("CreateJob error:", error);
			showSnack("NotifyNetworkError", "light");
		}
	});

	interface StateChange {
		jobId: string;
		oldState: JobState;
		newState: JobState;
		reason?: string;
		outcome?: ArchiveOutcome;
	}

	interface JobUpdate {
		jobId: string;
		company?: string;
		title?: string;
	}

	//https://keysandcastles-jason-mcburney-27c433854a5315ae0d36dddb2effab58b.gitlab.io/#operation/update-jobs
	const updateJobMutation = useMutation({
		mutationFn: async ({ jobId, ...updates }: JobUpdate) => {
			const nJobId = Number.parseInt(jobId);
			const apiClient = new ApiInterface(
				API_HTTP.resumeAnalyzer,
				user ?? undefined
			);
			const payload: UpdateJobsApiPayload = {
				stateChanges: [{ jobId: nJobId, ...updates }]
			};
			return apiClient.patch(resumeAnalyzerApiEndpoints.jobs, payload);
		},
		onMutate: ({ jobId, ...updates }: JobUpdate) => {
			const rollbackContext = {
				jobId,
				jobs: queryClient.getQueryData(["v2", "user", user?.uid, "jobs"]),
				job: queryClient.getQueryData(["job", jobId])
			};

			//Optimistically Update jobs list
			queryClient.setQueryData(
				["v2", "user", user?.uid, "jobs"],
				(old: JobCardData[] | undefined) =>
					old?.map((job) => (job.id === jobId ? { ...job, ...updates } : job))
			);

			//Optimistically Update job details
			queryClient.setQueryData(["job", jobId], (old: any) => {
				if (!old) return old;
				return {
					...old,
					job: {
						...old.job,
						...updates
					}
				};
			});

			return rollbackContext;
		},
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({
				queryKey: ["v2", "user", user?.uid, "jobs"]
			});
			queryClient.invalidateQueries({ queryKey: ["job", context?.jobId] });
			showSnack("NotifyJobUpdated", "dark");
		},
		onError: (error, variables, context) => {
			//Rollback Query Data.
			queryClient.setQueryData(
				["v2", "user", user?.uid, "jobs"],
				context?.jobs
			);
			queryClient.setQueryData(["job", context?.jobId], context?.job);
			console.log("updateJob error:", error);
			showSnack("NotifyNetworkError", "light");
		}
	});

	//https://keysandcastles-jason-mcburney-27c433854a5315ae0d36dddb2effab58b.gitlab.io/#operation/update-jobs
	const updateStateMutation = useMutation({
		mutationFn: async ({
			jobId,
			newState,
			oldState,
			reason,
			outcome
		}: StateChange) => {
			const nJobId = Number.parseInt(jobId);

			const apiClient = new ApiInterface(
				API_HTTP.resumeAnalyzer,
				user ?? undefined
			);

			const payload: UpdateJobsApiPayload = {
				stateChanges: [
					{ jobId: nJobId, newState, newOrder: 0, reason, outcome }
				]
			};
			return apiClient.patch(resumeAnalyzerApiEndpoints.jobs, payload);
		},
		onMutate: ({ jobId, newState, oldState }: StateChange) => {
			const rollbackContext = {
				jobId,
				newState: newState,
				jobs: queryClient.getQueryData(["v2", "user", user?.uid, "jobs"]),
				job: queryClient.getQueryData(["job", jobId])
			};

			//Optimistically Update.
			queryClient.setQueryData(
				["v2", "user", user?.uid, "jobs"],
				(old: JobCardData[] | undefined) =>
					old?.map((job) =>
						job.id === jobId ? { ...job, state: newState } : job
					)
			);

			queryClient.setQueryData(["job", jobId], (old: any) => {
				if (!old) return old;
				return {
					...old,
					job: {
						...old.job,
						state: newState
					}
				};
			});

			//Rollback context
			return rollbackContext;
		},
		onSuccess: (data, variables, context) => {
			queryClient.invalidateQueries({
				queryKey: ["v2", "user", user?.uid, "jobs"]
			});
			queryClient.invalidateQueries({ queryKey: ["job", context?.jobId] });
			queryClient.invalidateQueries({ queryKey: ["history", context?.jobId] });

			if (context?.newState !== DONE) {
				showSnack("NotifyJobUpdated", "dark");
			}
		},
		onError: (error, variables, context) => {
			//Rollback Query Data.
			queryClient.setQueryData(
				["v2", "user", user?.uid, "jobs"],
				context?.jobs
			);
			queryClient.setQueryData(["job", context?.jobId], context?.job);

			console.log("updateState error:", error);
			showSnack("NotifyNetworkError", "light");
		}
	});

	//Generate Coverletter
	// https://keysandcastles-jason-mcburney-27c433854a5315ae0d36dddb2effab58b.gitlab.io/#operation/generate-cover-letter
	const generateCoverletterMutation = useMutation({
		mutationKey: ["cover-letter", "generate"],
		mutationFn: (jobId: string): Promise<GenerateCoverLetterApiResponse> => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			const res = apiClient.post(resumeAnalyzerApiEndpoints.coverLetter, {
				jobId: parseInt(jobId, 10)
			});
			console.log(`CoverLetter Mutation Called, inproc`);
			return res;
		},
		onMutate: (jobId: string) => {
			// Preemptively increment "trial cover letters" stat and user details
			queryClient.setQueryData(["user", userId, "details"], (old: any) => {
				// Don't update the cache if there is no cache
				if (old === undefined) return old;
				// Don't update the cache if the user has the unlimited cover letters permission
				if (hasUnlimitedCoverLetters) return old;
				return {
					...old,
					usageStats: {
						...old.usageStats,
						trialCoverLetters: old.usageStats.trialCoverLetters + 1
					},
					trialCoverLettersRemaining: old.trialCoverLettersRemaining - 1
				};
			});
			queryClient.setQueryData(
				["job", jobId],
				(old: JobDetails | undefined): typeof old => {
					//console.log(old);
					return old ? { ...old, isGeneratingCoverLetter: true } : old;
				}
			);
		},
		onSuccess: (data, jobId) => {
			console.log("CoverLetter Mutation success");
			console.log(data);
			// Update the cached result of the get-latest-cover-letter query
			// pendingCoverLetterFileId is not included because it should not be present when the cover letter has finished generating
			queryClient.setQueryData(
				["coverLetter", jobId],
				(old: GetLatestCoverLetterApiResponse | undefined): typeof old =>
					old ? { coverLetter: data.coverLetter } : old
			);
			queryClient.setQueryData(
				["job", jobId],
				(old: JobDetails | undefined): typeof old => {
					if (!old) return old;
					return {
						...old,
						isGeneratingCoverLetter: false,
						coverLetterFileId: data.coverLetterFileId
					};
				}
			);
			queryClient.invalidateQueries({ queryKey: ["history", jobId] });
		},
		onError: (err) => {
			console.log(err);
		}
	});

	// Generate Resume
	// https://keysandcastles-jason-mcburney-27c433854a5315ae0d36dddb2effab58b.gitlab.io/#operation/generate-resume
	const generateResumeMutation = useMutation({
		mutationKey: ["resume", "generate"],
		mutationFn: (jobId: string): Promise<GenerateResumeApiResponse> => {
			const apiClient = new ApiInterface(API_HTTP.resumeAnalyzer, user);
			const res = apiClient.post(resumeAnalyzerApiEndpoints.resumeGenerate, {
				jobId: parseInt(jobId, 10)
			});
			console.log("Resume Mutation Called, inproc");
			return res;
		},
		onMutate: () => {
			// Preemptively increment "trial resumes" stat and user details
			queryClient.setQueryData(["user", userId, "details"], (old: any) => {
				// Don't update the cache if there is no cache
				if (old === undefined) return old;
				// Don't update the cache if the user has the unlimited resumes permission
				if (hasUnlimitedResumes) return old;
				return {
					...old,
					usageStats: {
						...old.usageStats,
						trialResumes: old.usageStats.trialResumes + 1
					},
					trialResumesRemaining: old.trialResumesRemaining - 1
				};
			});
		},
		onSuccess: (data, jobId) => {
			console.log("Resume Mutation success");
			console.log(data);
			queryClient.setQueryData(["resume", jobId], { resume: data.resume });
			queryClient.invalidateQueries({ queryKey: ["history", jobId] });
		},
		onError: (err) => {
			console.log(err);
		}
	});

	return {
		PostActiveJob: (
			data: PostJobData,
			generateCoverLetter: boolean = false,
			generateResume: boolean = false
		) => {
			// Initiate mutation
			createJobMutation
				.mutateAsync(data)
				.then(({ jobId }) => {
					if (generateCoverLetter) {
						generateCoverletterMutation.mutateAsync(jobId.toString());
					}
					if (generateResume) {
						generateResumeMutation.mutateAsync(jobId.toString());
					}
				})
				// Ignore errors, they are handled by onError in the mutations
				.catch(() => {});
		},
		UpdateJob: (
			jobId: string,
			updates: { company?: string; title?: string }
		) => {
			updateJobMutation.mutateAsync({ jobId, ...updates });
		},
		UpdateJobState: (
			jobId: string,
			oldState: JobState,
			newState: JobState,
			reason?: string,
			outcome?: ArchiveOutcome
		) => {
			updateStateMutation.mutateAsync({
				jobId,
				oldState,
				newState,
				reason,
				outcome
			});
		},
		GenerateCoverLetter: (jobId: string) =>
			generateCoverletterMutation.mutateAsync(jobId),
		GenerateResume: (jobId: string) =>
			generateResumeMutation.mutateAsync(jobId),
		getJobs
	};
};
